import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Calendar,
  Clock,
  DollarSign,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
  Trash2,
  Search,
  ChevronLeft,
  ChevronRight,
  FileText,
  MoreVertical,
  Bell,
  Hourglass,
} from "lucide-react";
import { educationAPI } from "../api/axiosClient";
import { Event, EventRegistration, Currency } from "../api/types";
import { toast } from "react-hot-toast";
import EventEditForm from "../components/EventEditForm";

interface EventDetailViewProps {
  eventId: string;
  schoolId: string;
  onBack: () => void;
  onDeleteConfirm: (id: string, type: string) => void;
  onSuccess: (action: "createEvent" | "updateEvent" | "deleteEvent") => void;
}

const EventDetailView: React.FC<EventDetailViewProps> = ({
  eventId,
  schoolId,
  onBack,
  onDeleteConfirm,
  onSuccess,
}) => {
  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showEditForm, setShowEditForm] = useState<boolean>(false);

  // Registrations state
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [registrationSearchTerm, setRegistrationSearchTerm] =
    useState<string>("");
  const [filteredRegistrations, setFilteredRegistrations] = useState<
    EventRegistration[]
  >([]);

  // Pagination for registrations
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // Dropdown and notification states
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [showNotificationModal, setShowNotificationModal] =
    useState<boolean>(false);
  const [selectedStudentForNotification, setSelectedStudentForNotification] =
    useState<EventRegistration | null>(null);
  const [showBulkNotificationModal, setShowBulkNotificationModal] =
    useState<boolean>(false);
  const [notificationChannels, setNotificationChannels] = useState({
    sms: false,
    push: false,
    email: false,
  });
  const [sendingNotification, setSendingNotification] =
    useState<boolean>(false);

  const fetchEventDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await educationAPI.getEventDetails(eventId);
      if (response.success && response.data) {
        setEvent(response.data as Event);
        // Registrations from event details
        const eventData = response.data as Event;
        setRegistrations(eventData.registrations || []);
        setFilteredRegistrations(eventData.registrations || []);
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
      toast.error("Etkinlik detayları yüklenirken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEventDetails();
  }, [fetchEventDetails]);

  // Filter registrations when search term changes
  useEffect(() => {
    if (!registrationSearchTerm.trim()) {
      setFilteredRegistrations(registrations);
    } else {
      const searchLower = registrationSearchTerm.toLowerCase();
      const filtered = registrations.filter(
        (reg) =>
          reg.studentName.toLowerCase().includes(searchLower) ||
          reg.studentNumber.toLowerCase().includes(searchLower) ||
          reg.classroomName.toLowerCase().includes(searchLower),
      );
      setFilteredRegistrations(filtered);
    }
    setCurrentPage(1);
  }, [registrationSearchTerm, registrations]);

  // Helper functions
  const getEventTypeLabel = (type: number): string => {
    switch (type) {
      case 0:
        return "Bilinmiyor";
      case 1:
        return "Gezi";
      case 2:
        return "Tatil";
      case 3:
        return "Konser";
      case 4:
        return "Tiyatro";
      case 5:
        return "Müze Ziyareti";
      case 6:
        return "Atölye";
      case 7:
        return "Seminer";
      case 8:
        return "Spor Etkinliği";
      case 9:
        return "Yarışma";
      case 10:
        return "Festival/Fuar";
      case 11:
        return "Mezuniyet";
      case 12:
        return "Oryantasyon";
      case 13:
        return "Piknik";
      case 14:
        return "Hayır/Bağış";
      case 15:
        return "Sınav";
      case 16:
        return "Okul Ödemesi";
      case 99:
        return "Diğer";
      default:
        return "Bilinmiyor";
    }
  };

  const getCurrencySymbol = (currency: Currency): string => {
    switch (currency) {
      case Currency.TRY:
        return "₺";
      default:
        return "₺";
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPrice = (price: number, currency: Currency): string => {
    if (price === 0) {
      return "Ücretsiz";
    }
    const symbol = getCurrencySymbol(currency);
    return `${symbol} ${price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const isEventActive = (startsAt: string, endsAt: string): boolean => {
    const now = new Date();
    const startDate = new Date(startsAt);
    const endDate = new Date(endsAt);
    return now >= startDate && now <= endDate;
  };

  const isEventUpcoming = (startsAt: string): boolean => {
    const now = new Date();
    const startDate = new Date(startsAt);
    return startDate > now;
  };

  const isEventPast = (endsAt: string): boolean => {
    const now = new Date();
    const endDate = new Date(endsAt);
    return endDate < now;
  };

  const getEventStatus = (
    startsAt: string,
    endsAt: string,
  ): { label: string; className: string } => {
    if (isEventActive(startsAt, endsAt)) {
      return { label: "Aktif", className: "bg-green-100 text-green-800" };
    } else if (isEventUpcoming(startsAt)) {
      return { label: "Yaklaşan", className: "bg-blue-100 text-blue-800" };
    } else if (isEventPast(endsAt)) {
      return { label: "Tamamlandı", className: "bg-gray-100 text-gray-800" };
    }
    return { label: "Bilinmiyor", className: "bg-gray-100 text-gray-800" };
  };

  const getRegistrationStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "approved":
      case "onaylandı":
        return {
          label: "Onaylandı",
          className: "bg-green-100 text-green-800",
          icon: CheckCircle,
        };
      case "pending":
      case "bekliyor":
        return {
          label: "Bekliyor",
          className: "bg-yellow-100 text-yellow-800",
          icon: AlertCircle,
        };
      case "rejected":
      case "reddedildi":
        return {
          label: "Reddedildi",
          className: "bg-red-100 text-red-800",
          icon: XCircle,
        };
      default:
        return {
          label: status,
          className: "bg-gray-100 text-gray-800",
          icon: AlertCircle,
        };
    }
  };

  const getPaymentStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "paid":
      case "ödendi":
        return { label: "Ödendi", className: "bg-green-100 text-green-800" };
      case "pending":
      case "bekliyor":
        return {
          label: "Bekliyor",
          className: "bg-yellow-100 text-yellow-800",
        };
      case "overdue":
      case "gecikmiş":
        return { label: "Gecikmiş", className: "bg-red-100 text-red-800" };
      default:
        return { label: status, className: "bg-gray-100 text-gray-800" };
    }
  };

  const handleEdit = () => {
    setShowEditForm(true);
  };

  const handleEditClose = () => {
    setShowEditForm(false);
    // Modal kapandığında her zaman veriyi yenile
    fetchEventDetails();
  };

  const handleEditSuccess = () => {
    setShowEditForm(false);
    fetchEventDetails();
    onSuccess("updateEvent");
  };

  const handleDelete = () => {
    if (event) {
      onDeleteConfirm(event.eventId, "event");
    }
  };

  // Toggle dropdown
  const handleToggleDropdown = (registrationId: string) => {
    setOpenDropdownId(
      openDropdownId === registrationId ? null : registrationId,
    );
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenDropdownId(null);
    if (openDropdownId) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [openDropdownId]);

  // Handle send notification to single student
  const handleSendNotification = (registration: EventRegistration) => {
    setSelectedStudentForNotification(registration);
    setShowNotificationModal(true);
    setOpenDropdownId(null);
  };

  // Handle delete registration
  const handleDeleteRegistration = (registration: EventRegistration) => {
    toast.success(`${registration.studentName} kaydı silindi`);
    setOpenDropdownId(null);
    // TODO: API call for delete registration
  };

  // Handle bulk notification
  const handleBulkNotification = () => {
    setShowBulkNotificationModal(true);
  };

  // Handle channel change for notifications
  const handleChannelChange = (
    channel: "sms" | "push" | "email",
    checked: boolean,
  ) => {
    setNotificationChannels((prev) => ({
      ...prev,
      [channel]: checked,
    }));
  };

  // Send notification
  const handleSendNotificationSubmit = async (isBulk: boolean) => {
    if (
      !notificationChannels.sms &&
      !notificationChannels.push &&
      !notificationChannels.email
    ) {
      toast.error("Lütfen en az bir bildirim kanalı seçin");
      return;
    }

    if (!event) {
      toast.error("Etkinlik bilgisi bulunamadı");
      return;
    }

    setSendingNotification(true);
    try {
      // Get student IDs based on bulk or single notification
      const studentIds = isBulk
        ? filteredRegistrations.map((reg) => reg.registrationId)
        : selectedStudentForNotification
          ? [selectedStudentForNotification.registrationId]
          : [];

      if (studentIds.length === 0) {
        toast.error("Bildirim gönderilecek öğrenci bulunamadı");
        return;
      }

      const response = await educationAPI.sendEventReminder({
        id: event.eventId,
        eventStudentIds: studentIds,
        sms: notificationChannels.sms,
        push: notificationChannels.push,
        email: notificationChannels.email,
        isAllUser: isBulk
      });

      if (response.success) {
        if (isBulk) {
          toast.success(`${studentIds.length} öğrenciye bildirim gönderildi`);
          setShowBulkNotificationModal(false);
        } else {
          toast.success(
            `${selectedStudentForNotification?.studentName} için bildirim gönderildi`,
          );
          setShowNotificationModal(false);
          setSelectedStudentForNotification(null);
        }
        setNotificationChannels({ sms: false, push: false, email: false });
      } else {
        toast.error(
          response.message || "Bildirim gönderilirken bir hata oluştu",
        );
      }
    } catch (error: any) {
      console.error("Error sending notification:", error);
      toast.error(
        error.response?.data?.message ||
          "Bildirim gönderilirken bir hata oluştu",
      );
    } finally {
      setSendingNotification(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredRegistrations.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentRegistrations = filteredRegistrations.slice(
    startIndex,
    endIndex,
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B51E0] mx-auto mb-4"></div>
          <p className="text-gray-500">Etkinlik detayları yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="space-y-6">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Geri Dön
        </Button>
        <Card>
          <CardContent className="py-16 text-center">
            <p className="text-gray-500">Etkinlik bulunamadı</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = getEventStatus(event.startsAt, event.endsAt);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Geri Dön
        </Button>
        <div className="flex items-center space-x-2">
          <Button variant="outline" onClick={handleEdit}>
            <Edit className="w-4 h-4 mr-2" />
            Düzenle
          </Button>
          <Button
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleDelete}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Sil
          </Button>
        </div>
      </div>

      {/* Event Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <div className="h-16 w-16 rounded-xl bg-[#9B51E0] flex items-center justify-center">
                <span className="text-white font-bold text-2xl">
                  {event.name.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <CardTitle className="text-2xl">{event.name}</CardTitle>
                <CardDescription className="mt-1">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                  >
                    {status.label}
                  </span>
                  <span className="ml-2 text-gray-500">
                    {getEventTypeLabel(event.eventType)}
                  </span>
                </CardDescription>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Start Date */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Başlangıç Tarihi</p>
                <p className="font-medium">{formatDate(event.startsAt)}</p>
              </div>
            </div>

            {/* End Date */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Bitiş Tarihi</p>
                <p className="font-medium">{formatDate(event.endsAt)}</p>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-start space-x-3">
              <div className="px-4 py-2 bg-green-100 rounded-lg">
                <span className="w-5 h-5 text-green-600">₺</span>
              </div>
              <div>
                <p className="text-sm text-gray-500">Fiyat</p>
                <p className="font-medium">
                  {formatPrice(event.price, event.currency)}
                </p>
              </div>
            </div>

            {/* Last Payment Date */}
            <div className="flex items-start space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Son Ödeme Tarihi</p>
                <p className="font-medium">
                  {formatDate(event.lastPaymentDate)}
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          {event.description && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-medium text-gray-500 mb-2">
                Açıklama
              </h4>
              <p className="text-gray-700">{event.description}</p>
            </div>
          )}

          {/* Statistics */}
          <div className="mt-6 pt-6 border-t">
            <h4 className="text-sm font-medium text-gray-500 mb-4">
              İstatistikler
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Users className="w-8 h-8 text-[#9B51E0]" />
                  <div>
                    <p className="text-2xl font-bold">
                      {event.totalRegistrations || 0}
                    </p>
                    <p className="text-sm text-gray-500">Toplam Kayıt</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold">
                      {event.confirmedRegistrations || 0}
                    </p>
                    <p className="text-sm text-gray-500">Onaylanan Kayıt</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                <span className="w-8 h-8 flex items-center justify-center text-blue-600 font-extrabold text-2xl leading-none">
                  ₺
                </span>
                
                  <div>
                    <p className="text-2xl font-bold">
                      {event.paidRegistrations || 0}
                    </p>
                    <p className="text-sm text-gray-500">Ödeme Yapılan</p>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Hourglass  className="w-8 h-8 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold">
                      {event.pendigRegistrations || 0}
                    </p>
                    <p className="text-sm text-gray-500">Bekleyen Ödeme</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Agreements Card */}
      {event.agreements && event.agreements.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              Sözleşmeler
            </CardTitle>
            <CardDescription>
              Bu etkinliğe atanmış sözleşme belgeleri
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {event.agreements.map((agreement) => (
                <div
                  key={agreement.eventAgreementDocumentId}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">{agreement.name}</p>
                      <p className="text-sm text-gray-500">
                        Versiyon: {agreement.version}
                      </p>
                    </div>
                  </div>
                  {agreement.isLatest && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Güncel
                    </span>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Registrations Table */}
      <Card className="overflow-visible">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center">
                <Users className="w-5 h-5 mr-2" />
                Kayıtlı Öğrenciler
              </CardTitle>
              <CardDescription>
                Bu etkinliğe kayıtlı öğrencilerin listesi
              </CardDescription>
            </div>
            {filteredRegistrations.length > 0 && (
              <Button
                className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                onClick={handleBulkNotification}
              >
                <Bell className="w-4 h-4 mr-2" />
                Herkese Bildirim Gönder
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="overflow-visible">
          {/* Search */}
          <div className="mb-4">
            <div className="relative max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Öğrenci ara..."
                value={registrationSearchTerm}
                onChange={(e) => setRegistrationSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
              />
            </div>
          </div>

          {/* Table */}
          <div className="w-full">
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Öğrenci
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Numara
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sınıf
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kayıt Durumu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ödeme Durumu
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kayıt Tarihi
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Veli
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRegistrations.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      {registrationSearchTerm
                        ? "Arama kriterlerine uygun kayıt bulunamadı"
                        : "Henüz kayıt bulunmuyor"}
                    </td>
                  </tr>
                ) : (
                  currentRegistrations.map((registration) => {
                    const regStatus = getRegistrationStatusBadge(
                      registration.statusDesc,
                    );
                    const payStatus = getPaymentStatusBadge(
                      registration.paymentStatusDesc,
                    );
                    const StatusIcon = regStatus.icon;
                    return (
                      <tr
                        key={registration.registrationId}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-[#9B51E0] flex items-center justify-center">
                                <span className="text-white font-semibold text-sm">
                                  {registration.studentName
                                    .charAt(0)
                                    .toUpperCase()}
                                </span>
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {registration.studentName}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {registration.studentNumber}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {registration.classroomName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${regStatus.className}`}
                          >
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {regStatus.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${payStatus.className}`}
                          >
                            {payStatus.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDateTime(registration.registeredAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {registration.parents &&
                            registration.parents.length > 0
                              ? registration.parents
                                  .map((p) => p.fullName)
                                  .join(", ")
                              : "-"}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleDropdown(registration.registrationId);
                            }}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                          {openDropdownId === registration.registrationId && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[100]">
                              {/* <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRefund(registration);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <RotateCcw className="w-4 h-4 mr-2" />
                                İade Et
                              </button> */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSendNotification(registration);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                              >
                                <Bell className="w-4 h-4 mr-2" />
                                Bildirim Gönder
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteRegistration(registration);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Sil
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <div className="text-sm text-gray-700">
                Toplam {filteredRegistrations.length} kayıt, Sayfa {currentPage}{" "}
                / {totalPages}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Form */}
      {showEditForm && (
        <EventEditForm
          isOpen={showEditForm}
          onClose={handleEditClose}
          onSuccess={handleEditSuccess}
          editingEventId={eventId}
          schoolId={schoolId}
        />
      )}

      {/* Single Student Notification Modal */}
      {showNotificationModal && selectedStudentForNotification && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Bildirim Gönder
                </h3>
              </div>

              <p className="text-gray-600 mb-4">
                <span className="font-medium">
                  {selectedStudentForNotification.studentName}
                </span>{" "}
                öğrencisinin velilerine bildirim gönderilecek.
              </p>

              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700">
                  Bildirim Kanalları
                </p>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationChannels.sms}
                      onChange={(e) =>
                        handleChannelChange("sms", e.target.checked)
                      }
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-600">SMS</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationChannels.push}
                      onChange={(e) =>
                        handleChannelChange("push", e.target.checked)
                      }
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-600">
                      Uygulama İçi Bildirim
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowNotificationModal(false);
                    setSelectedStudentForNotification(null);
                    setNotificationChannels({
                      sms: false,
                      push: false,
                      email: false,
                    });
                  }}
                  disabled={sendingNotification}
                >
                  İptal
                </Button>
                <Button
                  onClick={() => handleSendNotificationSubmit(false)}
                  disabled={sendingNotification}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                >
                  {sendingNotification ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Notification Modal */}
      {showBulkNotificationModal && (
        <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Herkese Bildirim Gönder
                </h3>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">
                    {filteredRegistrations.length}
                  </span>{" "}
                  öğrencinin velilerine bildirim gönderilecek.
                </p>
              </div>

              <div className="space-y-3 mb-6">
                <p className="text-sm font-medium text-gray-700">
                  Bildirim Kanalları
                </p>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationChannels.sms}
                      onChange={(e) =>
                        handleChannelChange("sms", e.target.checked)
                      }
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-600">SMS</span>
                  </label>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationChannels.push}
                      onChange={(e) =>
                        handleChannelChange("push", e.target.checked)
                      }
                      className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-600">
                      Uygulama İçi Bildirim
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowBulkNotificationModal(false);
                    setNotificationChannels({
                      sms: false,
                      push: false,
                      email: false,
                    });
                  }}
                  disabled={sendingNotification}
                >
                  İptal
                </Button>
                <Button
                  onClick={() => handleSendNotificationSubmit(true)}
                  disabled={sendingNotification}
                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                >
                  {sendingNotification ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <Bell className="w-4 h-4 mr-2" />
                      Herkese Gönder
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetailView;
